/**
 * @fileoverview Contexto responsável por carregar e sincronizar o progresso do
 * onboarding guiado da empresa. Segue o padrão do QueuesContext (guards de rota
 * pública/token, fetch on-mount, listener de socket). A decisão de exibir a UI
 * (admin-only) é tomada na camada de layout via AuthContext.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef
} from "react";
import { SocketContext } from "../Socket/SocketContext";
import api from "../../services/api";

const OnboardingContext = createContext();

const PUBLIC_ROUTES = ["/login", "/signup", "/forgetpsw"];

const isPublicRoute = () =>
  PUBLIC_ROUTES.some(route => window.location.pathname.startsWith(route));

export const OnboardingProvider = ({ children }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tour guiado (spotlight). activeTour = { tourId, i18nKey } | null.
  const [activeTour, setActiveTour] = useState(null);
  // Gamificação: última tarefa recém-concluída (dispara confetti/celebração).
  const [celebration, setCelebration] = useState(null);
  // Billing/trial: veredito de acesso vindo do painel (via backend /account/status).
  const [billing, setBilling] = useState(null);
  const socketManager = useContext(SocketContext);

  // Conjunto de tarefas já concluídas conhecidas — usado para detectar
  // transições -> done e disparar a celebração (sem celebrar no 1º load).
  const doneKeysRef = useRef(null);

  // Aplica novo progresso detectando tarefas recém-concluídas para gamificação.
  const applyProgress = useCallback(data => {
    if (!data || !Array.isArray(data.tasks)) {
      setProgress(data);
      return;
    }
    const doneNow = new Set(data.tasks.filter(t => t.status === "done").map(t => t.key));
    if (doneKeysRef.current === null) {
      // primeira carga: apenas semeia, não celebra estado pré-existente
      doneKeysRef.current = doneNow;
    } else {
      const fresh = [...doneNow].filter(k => !doneKeysRef.current.has(k));
      doneKeysRef.current = doneNow;
      if (fresh.length > 0) {
        // createAi (AHA) tem prioridade na celebração
        const taskKey = fresh.includes("createAi") ? "createAi" : fresh[0];
        setCelebration({
          taskKey,
          ahaDone: taskKey === "createAi",
          allRequiredDone: !!data.allRequiredDone,
          at: Date.now()
        });
      }
    }
    setProgress(data);
  }, []);

  const fetchProgress = useCallback(async () => {
    if (isPublicRoute()) {
      setLoading(false);
      return null;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return null;
    }
    try {
      setLoading(true);
      const { data } = await api.get("/onboarding/progress");
      applyProgress(data);
      return data;
    } catch (err) {
      // Best-effort: onboarding nunca deve quebrar a aplicação.
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyProgress]);

  const refetch = useCallback(() => fetchProgress(), [fetchProgress]);

  // Billing: GET /account/status (trial/pago/bloqueado/cancelado).
  const fetchBilling = useCallback(async (opts) => {
    if (isPublicRoute()) return null;
    const token = localStorage.getItem("token");
    if (!token) return null;
    try {
      // `fresh` (polling pós-checkout) pula o cache do backend p/ detectar
      // pago_ativo assim que o webhook do Pagar.me confirmar.
      const { data } = await api.get("/account/status", {
        params: opts?.fresh ? { fresh: 1 } : undefined
      });
      setBilling(data);
      return data;
    } catch (err) {
      return null; // best-effort: billing nunca quebra a aplicação
    }
  }, []);

  useEffect(() => {
    fetchProgress();
    fetchBilling();
    // Revalida o billing periodicamente (auto-release/expiração de trial).
    const interval = setInterval(fetchBilling, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchProgress, fetchBilling]);

  // Realtime: o backend emite `company-${companyId}-onboarding` na mainchannel.
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId || !socketManager) return undefined;

    const socket = socketManager.getSocket(companyId);
    const handleOnboarding = data => {
      if (data?.progress) applyProgress(data.progress);
    };
    const handleBilling = () => {
      fetchBilling();
    };
    socket.on(`company-${companyId}-onboarding`, handleOnboarding);
    socket.on(`company-${companyId}-billing`, handleBilling);

    return () => {
      socket.off(`company-${companyId}-onboarding`, handleOnboarding);
      socket.off(`company-${companyId}-billing`, handleBilling);
    };
  }, [socketManager, applyProgress, fetchBilling]);

  const completeTask = useCallback(async taskKey => {
    const { data } = await api.post(`/onboarding/tasks/${taskKey}/complete`);
    applyProgress(data);
    return data;
  }, [applyProgress]);

  const skipTask = useCallback(async taskKey => {
    const { data } = await api.post(`/onboarding/tasks/${taskKey}/skip`);
    applyProgress(data);
    return data;
  }, [applyProgress]);

  const patchState = useCallback(async patch => {
    const { data } = await api.patch("/onboarding/state", patch);
    applyProgress(data);
    return data;
  }, [applyProgress]);

  const setMinimized = useCallback(minimized => patchState({ minimized }), [patchState]);
  const setDismissed = useCallback(dismissed => patchState({ dismissed }), [patchState]);

  // Tour guiado
  const startTour = useCallback(task => {
    setActiveTour({ tourId: task.key, i18nKey: task.i18nKey });
  }, []);
  const endTour = useCallback(() => setActiveTour(null), []);

  // Gamificação
  const clearCelebration = useCallback(() => setCelebration(null), []);

  const value = {
    progress,
    loading,
    refetch,
    completeTask,
    skipTask,
    setMinimized,
    setDismissed,
    activeTour,
    startTour,
    endTour,
    celebration,
    clearCelebration,
    billing,
    refetchBilling: fetchBilling
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding deve ser usado dentro de um OnboardingProvider");
  }
  return context;
};

export default OnboardingContext;
